import { useEffect, useState, type ReactNode } from 'react';
import type { LayoutPatchOperation } from '@shared/contracts/customisation';
import {
  getEffectiveView,
  resetLayoutToDefault,
  rollbackLayout,
  saveLayout,
  simulateRemoteLayoutChange,
  type StoredView,
} from '@/mocks/layoutApi';
import { useAsync, useMutation } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { setOffline } from '@/mocks/db';
import { newIdempotencyKey } from '@/services/idempotency';
import { clearDraft, loadDraft, saveDraft } from '@/services/recoveryDraft';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { SectionHeader, StatusBadge } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { ConfirmSheet, Sheet } from '@/components/feedback/Sheet';
import { useToast } from '@/components/feedback/Toast';
import { widgetById, type MobileWidgetId } from '@/customisation/widgetRegistry';
import { describeOperations, validateOperations } from '@/customisation/policyEngine';
import { addableWidgets, proposeLayout, type ComposerResult } from '@/ai/composer';
import { isMockApiError } from '@/mocks/errors';

/**
 * Mobile home personalisation and the AI compact-layout composer.
 *
 * TRACE:UADD-V5-SMEM-PERS-001-01 — a consistently named "Edit home" entry.
 * TRACE:UADD-V5-SMEM-PERS-001-02 — add a permitted widget from a curated catalogue.
 * TRACE:UADD-V5-SMEM-PERS-001-03 — reorder by drag AND by non-drag controls.
 * TRACE:UADD-V5-SMEM-PERS-001-04 — remove with confirmation and Undo.
 * TRACE:UADD-V5-SMEM-PERS-001-06 — persists across sessions; reset restores the
 *   governed default.
 * TRACE:UADD-V5-SMEM-PERS-003-01 — target size, focus, naming and announcements.
 * TRACE:UADD-V5-SMEM-PERS-003-03 — offline save keeps a local draft.
 * TRACE:UADD-V5-SMEM-PERS-003-04 — a stale concurrent edit offers compare /
 *   reload / keep-mine and never overwrites silently.
 *
 * Every mutation — drag, non-drag control, catalogue add, AI apply — goes
 * through the same `commit()` path, so all of them produce the same persisted
 * layout, the same version record and the same audit event.
 */

const DRAFT_KEY = 'mobile-home-layout';

/**
 * Shared stroke attributes for the per-row move/position icons, matching the
 * line-icon language used elsewhere (see src/features/more/MoreIcons.tsx and
 * the marks in src/components/banking/index.tsx). Purely decorative — each
 * icon carries aria-hidden and every button's accessible name still comes
 * entirely from its visually-hidden text, exactly as for the per-widget-title
 * suffixes already used throughout this file.
 */
const WIDGET_CONTROL_ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true as const,
  className: 'tm-widget-controls__icon',
};

const WIDGET_CONTROL_ICONS: Record<'up' | 'down' | 'top' | 'bottom' | 'position', ReactNode> = {
  up: (
    <svg {...WIDGET_CONTROL_ICON_PROPS}>
      <path d="M12 19V6" />
      <path d="M6.5 11.5 12 6l5.5 5.5" />
    </svg>
  ),
  down: (
    <svg {...WIDGET_CONTROL_ICON_PROPS}>
      <path d="M12 5v13" />
      <path d="M6.5 12.5 12 18l5.5-5.5" />
    </svg>
  ),
  top: (
    <svg {...WIDGET_CONTROL_ICON_PROPS}>
      <path d="M5 5.5h14" />
      <path d="M12 19V10" />
      <path d="M8 13l4-4 4 4" />
    </svg>
  ),
  bottom: (
    <svg {...WIDGET_CONTROL_ICON_PROPS}>
      <path d="M5 18.5h14" />
      <path d="M12 5v9" />
      <path d="M8 11l4 4 4-4" />
    </svg>
  ),
  position: (
    <svg {...WIDGET_CONTROL_ICON_PROPS}>
      <circle cx="12" cy="10" r="2.6" />
      <path d="M12 21s6.5-6.1 6.5-11A6.5 6.5 0 0 0 5.5 10c0 4.9 6.5 11 6.5 11Z" />
    </svg>
  ),
};

export function PersonalisationPage() {
  const resolved = resolveContext();
  const toast = useToast();

  const [order, setOrder] = useState<readonly MobileWidgetId[]>([]);
  const [baseline, setBaseline] = useState<StoredView | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [conflict, setConflict] = useState<StoredView | null>(null);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [positionFor, setPositionFor] = useState<MobileWidgetId | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<MobileWidgetId | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [dragging, setDragging] = useState<MobileWidgetId | null>(null);

  // AI composer state.
  const [aiOpen, setAiOpen] = useState(false);
  const [goalText, setGoalText] = useState('');
  const [composed, setComposed] = useState<ComposerResult | null>(null);

  const ctx = resolved?.entitlements ?? null;

  const view = useAsync(async () => {
    if (!ctx) throw new Error('no session');
    return (await getEffectiveView(ctx)).data;
  }, [ctx?.user.id, ctx?.company.id]);

  useEffect(() => {
    if (view.status !== 'success') return;
    setBaseline(view.data);
    // Prefer an offline draft saved against this exact version.
    const draft = loadDraft<{ order: MobileWidgetId[] }>(DRAFT_KEY);
    if (draft && draft.baseVersion === view.data.version && draft.baseEtag === view.data.etag) {
      setOrder(draft.payload.order);
      setAnnouncement('Restored your unsaved changes from this device.');
    } else {
      setOrder(view.data.order);
    }
  }, [view.status, view.data]);

  const saveMutation = useMutation(
    async (operations: readonly LayoutPatchOperation[], summary: string, aiProposalId?: string) => {
      if (!ctx || !baseline) throw new Error('no baseline');
      return (
        await saveLayout({
          ctx,
          operations,
          baseVersion: baseline.version,
          baseEtag: baseline.etag,
          idempotencyKey: newIdempotencyKey('layout'),
          changeSummary: summary,
          ...(aiProposalId ? { aiProposalId } : {}),
        })
      ).data;
    },
  );

  if (!resolved || !ctx) return null;

  /** The single write path shared by drag, non-drag controls, catalogue and AI. */
  const commit = async (
    operations: readonly LayoutPatchOperation[],
    summary: string,
    options?: { aiProposalId?: string; undoTo?: readonly MobileWidgetId[] },
  ) => {
    const policy = validateOperations(ctx, order, operations);
    if (!policy.valid) {
      toast.show({ message: policy.violations[0] ?? 'That change is not allowed.' });
      setAnnouncement(policy.violations.join(' '));
      return;
    }

    const previous = order;
    const outcome = await saveMutation.run(operations, summary, options?.aiProposalId);

    if (outcome.ok) {
      setOrder(outcome.value.order);
      setBaseline(outcome.value);
      clearDraft(DRAFT_KEY);
      setAnnouncement(`${summary}. Saved.`);
      toast.show({
        message: summary,
        actionLabel: 'Undo',
        onAction: () => {
          void undoTo(options?.undoTo ?? previous, outcome.value);
        },
      });
      return;
    }

    if (isMockApiError(outcome.error) && outcome.error.code === 'VERSION_CONFLICT') {
      // Never overwrite silently: keep the user's work and offer a choice.
      saveDraft(DRAFT_KEY, {
        savedAt: new Date().toISOString(),
        baseVersion: baseline?.version ?? 0,
        baseEtag: baseline?.etag ?? '',
        payload: { order: previous },
      });
      const fresh = (await getEffectiveView(ctx)).data;
      setConflict(fresh);
      return;
    }

    if (isMockApiError(outcome.error) && outcome.error.code === 'OFFLINE') {
      saveDraft(DRAFT_KEY, {
        savedAt: new Date().toISOString(),
        baseVersion: baseline?.version ?? 0,
        baseEtag: baseline?.etag ?? '',
        payload: { order: applyLocally(previous, operations) },
      });
      setOrder(applyLocally(previous, operations));
      setAnnouncement('You are offline. Your changes are saved on this device and will sync later.');
      toast.show({ message: 'Saved on this device. We will sync when you reconnect.' });
      return;
    }

    toast.show({
      message: isMockApiError(outcome.error) ? outcome.error.message : 'We could not save that.',
    });
  };

  /** Restore a prior order by generating the inverse move set. */
  const undoTo = async (target: readonly MobileWidgetId[], from: StoredView) => {
    const ops: LayoutPatchOperation[] = [];
    let n = 0;
    for (const id of from.order) {
      if (!target.includes(id)) {
        n += 1;
        ops.push({ operationId: `u${n}`, type: 'remove', widgetId: id });
      }
    }
    target.forEach((id, index) => {
      if (!from.order.includes(id)) {
        n += 1;
        ops.push({ operationId: `u${n}`, type: 'add', widgetId: id });
      }
      n += 1;
      ops.push({
        operationId: `u${n}`,
        type: 'move',
        widgetId: id,
        breakpoint: 'narrow',
        position: { x: 0, y: index, w: 4, h: 1 },
      });
    });

    const outcome = await saveMutation.run(
      ops.filter((o) => {
        // Pinned widgets reject moves; drop those from the undo set.
        const id = o.widgetId as MobileWidgetId | undefined;
        return !(o.type === 'move' && id && widgetById(id)?.pinnedIndex !== undefined);
      }),
      'Undid the last home change',
    );
    if (outcome.ok) {
      setOrder(outcome.value.order);
      setBaseline(outcome.value);
      setAnnouncement('Change undone.');
    }
  };

  const movable = order.filter((id) => widgetById(id)?.pinnedIndex === undefined);

  const moveTo = (id: MobileWidgetId, targetIndexInMovable: number) => {
    const clamped = Math.max(0, Math.min(targetIndexInMovable, movable.length - 1));
    const pinnedBefore = order.findIndex((w) => widgetById(w)?.pinnedIndex === undefined);
    const absolute = (pinnedBefore < 0 ? 0 : pinnedBefore) + clamped;
    const definition = widgetById(id);
    void commit(
      [
        {
          operationId: 'm1',
          type: 'move',
          widgetId: id,
          breakpoint: 'narrow',
          position: { x: 0, y: absolute, w: 4, h: 1 },
        },
      ],
      `Moved "${definition?.title ?? id}" to position ${clamped + 1}`,
    );
  };

  const indexOfMovable = (id: MobileWidgetId) => movable.indexOf(id);

  return (
    <>
      <MobileHeader
        title="Edit home"
        backTo="/mobile/home"
        backLabel="Home"
        trailing={
          <button
            type="button"
            className="tm-btn tm-btn--quiet tm-btn--sm"
            onClick={() => {
              setComposed(null);
              setGoalText('');
              setAiOpen(true);
            }}
          >
            Ask AI
          </button>
        }
      />

      {/* Single live region for every reorder, add, remove and save result. */}
      <div
        role="status"
        aria-live="polite"
        aria-label="Layout changes"
        className="tm-visually-hidden"
      >
        {announcement}
      </div>

      <div className="tm-stack">
        {view.status === 'loading' ? (
          <LoadingRegion label="Loading your home layout">
            <CardSkeleton height={280} />
          </LoadingRegion>
        ) : view.status === 'error' ? (
          <ErrorState error={view.error} onRetry={view.reload} />
        ) : (
          <>
            <Notice tone="info" title="What you can change">
              You can add, remove and reorder the optional panels. The balance, quick actions and
              &ldquo;Needs attention&rdquo; are always shown and cannot be removed.
            </Notice>

            <section aria-labelledby="pers-list">
              <SectionHeader
                title="Your home panels"
                id="pers-list"
                action={
                  <button
                    type="button"
                    className="tm-btn tm-btn--quiet tm-btn--sm"
                    onClick={() => setCatalogueOpen(true)}
                  >
                    Add widget
                  </button>
                }
              />

              <ul className="tm-rows tm-widget-list">
                {order.map((id) => {
                  const definition = widgetById(id);
                  if (!definition) return null;
                  const pinned = definition.pinnedIndex !== undefined;
                  const mIndex = indexOfMovable(id);
                  return (
                    <li
                      key={id}
                      className={`tm-widget-row${dragging === id ? ' tm-widget-row--dragging' : ''}`}
                      draggable={!pinned}
                      onDragStart={() => setDragging(id)}
                      onDragEnd={() => setDragging(null)}
                      onDragOver={(e) => {
                        if (dragging && !pinned) e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragging && dragging !== id && !pinned) {
                          moveTo(dragging, indexOfMovable(id));
                        }
                        setDragging(null);
                      }}
                    >
                      <div className="tm-between">
                        <div className="tm-grow">
                          <strong>{definition.title}</strong>
                          <p className="tm-hint" style={{ margin: 0 }}>
                            {definition.description}
                          </p>
                        </div>
                        {pinned ? (
                          <StatusBadge status="locked" label="Always shown" tone="neutral" />
                        ) : null}
                      </div>

                      {/* Non-drag controls with exactly the mandated labels.
                          These produce the same persisted layout, version and
                          audit event as a drag. The four moves plus the
                          position picker are one compact icon toolbar rather
                          than five identical grey pills; each button's
                          accessible name is still its full mandated text,
                          carried entirely by visually-hidden spans exactly
                          like the per-widget-title suffixes below. */}
                      {!pinned ? (
                        <div className="tm-widget-controls">
                          <div className="tm-widget-controls__moves">
                            <button
                              type="button"
                              className="tm-icon-btn"
                              disabled={mIndex <= 0}
                              onClick={() => moveTo(id, mIndex - 1)}
                            >
                              {WIDGET_CONTROL_ICONS.up}
                              <span className="tm-visually-hidden">Move up</span>
                              <span className="tm-visually-hidden"> {definition.title}</span>
                            </button>
                            <button
                              type="button"
                              className="tm-icon-btn"
                              disabled={mIndex >= movable.length - 1}
                              onClick={() => moveTo(id, mIndex + 1)}
                            >
                              {WIDGET_CONTROL_ICONS.down}
                              <span className="tm-visually-hidden">Move down</span>
                              <span className="tm-visually-hidden"> {definition.title}</span>
                            </button>
                            <button
                              type="button"
                              className="tm-icon-btn"
                              disabled={mIndex <= 0}
                              onClick={() => moveTo(id, 0)}
                            >
                              {WIDGET_CONTROL_ICONS.top}
                              <span className="tm-visually-hidden">Move to top</span>
                              <span className="tm-visually-hidden"> {definition.title}</span>
                            </button>
                            <button
                              type="button"
                              className="tm-icon-btn"
                              disabled={mIndex >= movable.length - 1}
                              onClick={() => moveTo(id, movable.length - 1)}
                            >
                              {WIDGET_CONTROL_ICONS.bottom}
                              <span className="tm-visually-hidden">Move to bottom</span>
                              <span className="tm-visually-hidden"> {definition.title}</span>
                            </button>
                            <button
                              type="button"
                              className="tm-icon-btn"
                              onClick={() => setPositionFor(id)}
                            >
                              {WIDGET_CONTROL_ICONS.position}
                              <span className="tm-visually-hidden">Choose position</span>
                              <span className="tm-visually-hidden"> for {definition.title}</span>
                            </button>
                          </div>
                          {definition.removable ? (
                            <button
                              type="button"
                              className="tm-btn tm-btn--quiet tm-btn--sm tm-widget-controls__remove"
                              onClick={() => setConfirmRemove(id)}
                            >
                              Remove<span className="tm-visually-hidden"> {definition.title}</span>
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>

            <div className="tm-stack tm-stack--tight">
              <button
                type="button"
                className="tm-btn tm-btn--secondary tm-btn--block"
                onClick={() => setConfirmReset(true)}
              >
                Reset to default
              </button>
              {/* Demo control for the offline-recovery path. */}
              <button
                type="button"
                className="tm-btn tm-btn--quiet"
                onClick={() => {
                  setOffline(true);
                  toast.show({ message: 'Simulating offline. Your next change is kept on device.' });
                }}
              >
                Simulate going offline
              </button>
              <button
                type="button"
                className="tm-btn tm-btn--quiet"
                onClick={() => {
                  simulateRemoteLayoutChange(ctx);
                  toast.show({ message: 'Simulated a change from another device.' });
                }}
              >
                Simulate a change on another device
              </button>
            </div>

            <p className="tm-hint">
              Layout version {baseline?.version ?? 1}. Changes save to your account, so they follow
              you to your other devices.
            </p>
          </>
        )}
      </div>

      {/* ---------------------------------------------------- catalogue -- */}
      <Sheet open={catalogueOpen} onClose={() => setCatalogueOpen(false)} title="Add a widget">
        {addableWidgets(ctx, order).length === 0 ? (
          <p className="tm-muted">Every widget available to you is already on your home.</p>
        ) : (
          <ul className="tm-rows">
            {addableWidgets(ctx, order).map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="tm-row"
                  onClick={() => {
                    setCatalogueOpen(false);
                    void commit(
                      [{ operationId: 'a1', type: 'add', widgetId: c.id }],
                      `Added "${c.title}" to your home`,
                    );
                  }}
                >
                  <span className="tm-row__body">
                    <span className="tm-row__title">{c.title}</span>
                    <span className="tm-row__meta">{c.description}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Sheet>

      {/* ---------------------------------------------- choose position -- */}
      <Sheet
        open={positionFor !== null}
        onClose={() => setPositionFor(null)}
        title={`Choose position for ${positionFor ? (widgetById(positionFor)?.title ?? '') : ''}`}
      >
        <ul className="tm-rows">
          {movable.map((_, index) => (
            <li key={index}>
              <button
                type="button"
                className="tm-row"
                onClick={() => {
                  if (positionFor) moveTo(positionFor, index);
                  setPositionFor(null);
                }}
              >
                <span className="tm-row__body">
                  <span className="tm-row__title">Position {index + 1}</span>
                  <span className="tm-row__meta">
                    {index === 0
                      ? 'First of the optional panels'
                      : index === movable.length - 1
                        ? 'Last'
                        : `After "${widgetById(movable[index - 1]!)?.title ?? ''}"`}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>

      {/* ------------------------------------------------------- remove -- */}
      <ConfirmSheet
        open={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => {
          const id = confirmRemove;
          setConfirmRemove(null);
          if (!id) return;
          void commit(
            [{ operationId: 'r1', type: 'remove', widgetId: id }],
            `Removed "${widgetById(id)?.title ?? id}" from your home`,
            { undoTo: order },
          );
        }}
        title="Remove this widget?"
        body="You can add it back at any time from the widget catalogue, and you can undo straight after."
        confirmLabel="Remove widget"
      />

      {/* -------------------------------------------------------- reset -- */}
      <ConfirmSheet
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          setConfirmReset(false);
          void resetLayoutToDefault(ctx).then((response) => {
            setOrder(response.data.order);
            setBaseline(response.data);
            clearDraft(DRAFT_KEY);
            setAnnouncement('Home layout reset to the default.');
            toast.show({ message: 'Home layout reset to the default.' });
          });
        }}
        title="Reset your home?"
        body="This puts every panel back to the default arrangement for your role. Your other settings are unaffected."
        confirmLabel="Reset to default"
      />

      {/* ----------------------------------------------------- conflict -- */}
      <Sheet
        open={conflict !== null}
        onClose={() => setConflict(null)}
        title="Changed on another device"
      >
        <p className="tm-muted">
          Your home layout was changed somewhere else while you were editing. Nothing has been
          overwritten — choose what to keep.
        </p>
        <div className="tm-stack tm-stack--tight" style={{ marginBlockStart: 14 }}>
          <div className="tm-card">
            <strong>Their version</strong>
            <p className="tm-hint" style={{ margin: '2px 0 0' }}>
              {(conflict?.order ?? []).map((id) => widgetById(id)?.title ?? id).join(', ')}
            </p>
          </div>
          <div className="tm-card">
            <strong>Your version</strong>
            <p className="tm-hint" style={{ margin: '2px 0 0' }}>
              {order.map((id) => widgetById(id)?.title ?? id).join(', ')}
            </p>
          </div>
          <button
            type="button"
            className="tm-btn tm-btn--block"
            onClick={() => {
              if (conflict) {
                setOrder(conflict.order);
                setBaseline(conflict);
                clearDraft(DRAFT_KEY);
              }
              setConflict(null);
              setAnnouncement('Loaded the version from your other device.');
            }}
          >
            Use their version
          </button>
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => {
              if (conflict) setBaseline(conflict);
              setConflict(null);
              setAnnouncement('Keeping your version. Save again to publish it.');
            }}
          >
            Keep mine and save again
          </button>
        </div>
      </Sheet>

      {/* --------------------------------------------------- AI composer -- */}
      <Sheet open={aiOpen} onClose={() => setAiOpen(false)} title="Ask AI to arrange my home">
        <div className="tm-stack">
          {composed === null ? (
            <>
              <Notice tone="info" title="What the assistant can do">
                It can only rearrange the panels you are already entitled to. It cannot move money,
                approve anything, change permissions, or see your balances.
              </Notice>
              <div className="tm-field">
                <label className="tm-label" htmlFor="ai-goal">
                  What do you want your home to help you do?
                </label>
                <textarea
                  id="ai-goal"
                  className="tm-textarea"
                  rows={3}
                  value={goalText}
                  onChange={(e) => setGoalText(e.target.value)}
                  placeholder="For example: I mostly approve payments, keep it short"
                />
                <div className="tm-field__error-slot" />
              </div>
              <button
                type="button"
                className="tm-btn tm-btn--block"
                disabled={goalText.trim().length < 3}
                onClick={() => setComposed(proposeLayout(ctx, order, goalText))}
              >
                Suggest a layout
              </button>
              <p className="tm-hint">Nothing changes until you review and apply a suggestion.</p>
            </>
          ) : (
            <AiProposalView
              result={composed}
              currentOrder={order}
              onBack={() => setComposed(null)}
              onApply={() => {
                const { proposal } = composed;
                setAiOpen(false);
                const previous = order;
                void commit(proposal.operations, 'Applied an AI home layout suggestion', {
                  aiProposalId: proposal.proposalId,
                  undoTo: previous,
                });
                setComposed(null);
              }}
            />
          )}
        </div>
      </Sheet>
    </>
  );
}

/** Local-only application, used when offline so the UI still reflects intent. */
function applyLocally(
  current: readonly MobileWidgetId[],
  operations: readonly LayoutPatchOperation[],
): MobileWidgetId[] {
  let next = [...current];
  for (const op of operations) {
    const id = op.widgetId as MobileWidgetId | undefined;
    if (!id) continue;
    if (op.type === 'add' && !next.includes(id)) next.push(id);
    if (op.type === 'remove') next = next.filter((w) => w !== id);
    if (op.type === 'move' && typeof op.position?.y === 'number') {
      next = next.filter((w) => w !== id);
      next.splice(Math.min(op.position.y, next.length), 0, id);
    }
  }
  return next;
}

/** Preview, operation-by-operation diff, rationale and uncertainty. */
function AiProposalView({
  result,
  currentOrder,
  onApply,
  onBack,
}: {
  result: ComposerResult;
  currentOrder: readonly MobileWidgetId[];
  onApply: () => void;
  onBack: () => void;
}) {
  const { proposal, policy, assessment, schemaValid } = result;
  const diff = describeOperations(currentOrder, proposal.operations);

  const blocked = !schemaValid || !policy.valid;
  const needsClarification = proposal.clarifyingQuestions.length > 0;

  return (
    <>
      {assessment.injectionSuspected ? (
        <Notice tone="warning" title="Part of your message was ignored">
          Something in that request looked like an instruction to change access or move money. The
          assistant can only rearrange your home page, so that part was not acted on.
        </Notice>
      ) : null}

      {needsClarification ? (
        <>
          <Notice tone="info" title="I need a bit more to go on">
            <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
              {proposal.clarifyingQuestions.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
          </Notice>
          <button type="button" className="tm-btn tm-btn--secondary tm-btn--block" onClick={onBack}>
            Describe it differently
          </button>
        </>
      ) : blocked ? (
        <>
          <Notice tone="danger" title="I cannot make that change">
            <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
              {policy.violations.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          </Notice>
          <p className="tm-hint">Your home is unchanged.</p>
          <button type="button" className="tm-btn tm-btn--secondary tm-btn--block" onClick={onBack}>
            Try a different request
          </button>
        </>
      ) : (
        <>
          <section aria-labelledby="ai-diff">
            <SectionHeader title="What would change" id="ai-diff" />
            {diff.length === 0 ? (
              <p className="tm-muted">Nothing would change — your home already suits that.</p>
            ) : (
              <ul className="tm-rows">
                {diff.map((line) => (
                  <li key={line.text} className="tm-diff-row">
                    {/* Verb label plus text, never colour alone. */}
                    <span className={`tm-badge tm-badge--${line.kind === 'Remove' ? 'danger' : line.kind === 'Add' ? 'success' : 'info'}`}>
                      {line.kind}
                    </span>
                    <span>{line.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="ai-preview">
            <SectionHeader title="Preview" id="ai-preview" />
            <div className="tm-card tm-preview">
              <ol style={{ margin: 0, paddingInlineStart: 18 }}>
                {applyLocally(currentOrder, proposal.operations).map((id) => (
                  <li key={id}>{widgetById(id)?.title ?? id}</li>
                ))}
              </ol>
              <p className="tm-hint" style={{ marginBlockStart: 8 }}>
                Preview only — this is not applied yet.
              </p>
            </div>
          </section>

          {proposal.rationale.length > 0 ? (
            <section aria-labelledby="ai-why">
              <SectionHeader title="Why" id="ai-why" />
              <ul className="tm-muted" style={{ margin: 0, paddingInlineStart: 18 }}>
                {proposal.rationale.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </section>
          ) : null}

          {proposal.uncertainty.length > 0 ? (
            <section aria-labelledby="ai-unsure">
              <SectionHeader title="What I am unsure about" id="ai-unsure" />
              <ul className="tm-muted" style={{ margin: 0, paddingInlineStart: 18 }}>
                {proposal.uncertainty.map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="tm-stack tm-stack--tight">
            <button
              type="button"
              className="tm-btn tm-btn--block"
              disabled={diff.length === 0}
              onClick={onApply}
            >
              Apply these changes
            </button>
            <button type="button" className="tm-btn tm-btn--secondary tm-btn--block" onClick={onBack}>
              Not this — try again
            </button>
          </div>
        </>
      )}
    </>
  );
}

export { rollbackLayout };
